/**
 * 프로젝트 도메인 타입
 *
 * projects 테이블(백엔드 저장소) 및 관련 타입 정의
 * MAIN-001, MDH-001 지원을 위한 계층형 데이터 구조
 */

import type { CoSimConfig, NmlCouplingConfig } from './cosim';
import type { Scenario } from '@/domain/scenario/schema';
import type { NodeSizingRules } from './mars';
import type { ScopeId, ModelRole } from './scope';
import { TERMINAL_SIM_STATUSES, isInProgressSimStatus, isSimStatus, type SimStatus } from './simulation';

export type { ScopeGroup, ScopeId, ModelRole } from './scope';

// ============================================
// 기본 타입 정의
// ============================================

/**
 * @deprecated Category는 더 이상 사용하지 않음 - DB 스키마에서 제거 예정
 */
export type ProjectCategory = 'nuclear' | 'power' | 'control';

/** 계통 스코프 id — 그룹(`primary`|`secondary`|`bop`, 종전 값) 또는 `그룹/계통`(예 `primary/sg`).
 *  카탈로그·헬퍼는 types/scope.ts. 이름은 하위 호환으로 유지. */
export type SystemScope = ScopeId;

// 분석 코드 타입 (복수 선택 가능)
export type AnalysisCode = 'MARS' | 'GAMMA+' | 'Modelica';

// 편집 모드 타입

// MARS Problem Type (Card 100 W1)
export type MARSProblemType = 'NEW' | 'RESTART';

// MARS Problem Option (Card 100 W2)
export type MARSProblemOption = 'STDY-ST' | 'TRANSNT';

// RESTART 소스 참조 정보 (Card 103)
export interface RestartSource {
  projectId: string;          // 소스 프로젝트 ID
  projectName: string;        // 표시용 (denormalized)
  modelId: string;            // 소스 모델 ID
  modelName: string;          // 표시용 (denormalized)
  simulationId?: string;      // 특정 시뮬레이션 run
  /** 그 실행 안의 참가자 순번 — 산출물 디렉터리를 짚는 좌표. 단독 실행은 0 */
  taskIndex?: number;
  restartNumber: number;      // restart block 번호 (-1 = 마지막)
  rstpltPath?: string;        // MinIO 경로 (auto-resolved)
}

// MARS 설정 (파티션별)
export interface MARSConfig {
  problemType: MARSProblemType;       // NEW | RESTART
  problemOption: MARSProblemOption;   // STDY-ST | TRANSNT
  restartSource?: RestartSource;      // RESTART일 때만
}

// ============================================
// Model 관련 타입 (MAIN-001, MDH-001)
// ============================================

// 모델 스코프 (계통 + 컴포넌트)
export interface ModelScope {
  systems: SystemScope[];    // 계통 레벨: ['primary'](그룹 전체) 또는 ['primary/sg', 'secondary/feedwater'] 등
  components: string[];      // 개별 컴포넌트 ID들 (선택적)
}

// 버전 히스토리 엔트리
export interface VersionEntry {
  /** 이 기록의 신원 — 만드는 쪽이 싣는다. 같은 기록이 두 번 얹히는 것을 가르는 유일한 근거다(옛 기록엔 없다) */
  id?: string;
  version: string;           // "1.0", "1.1", "1.2"
  timestamp: string;         // ISO timestamp
  author: string;            // 작성자 이름/ID
  description: string;       // "Nodalization Diagram 수정"
}

// 시뮬레이션 히스토리 엔트리
/** 참가자 하나의 종결 판정(원문 라벨: completed|failed|stopped|terminated|unspecified) */
export interface ParticipantVerdict {
  taskId: string;
  finalState: string;
  /** 이 참가자가 왜 끝났는지 — 어댑터가 보고한 원문. 보고가 없었거나 구 이력이면 필드가 없다 */
  detail?: string;
}

/** 참가자 하나의 신원 — 실행 기록(BFF simulation_history.participant_specs)의 **시작 시점 스냅샷**. 실행 기록이
 *  스스로를 설명하는 근거: 프로젝트가 지워지거나 모델이 개명돼도 이어보기·결과 보기가 그때의 이름·종별로 부른다.
 *  모르는 값은 필드가 없다(구 이력·미기록). */
export interface ParticipantSpec {
  taskId: string;
  modelId?: string;
  modelName?: string;
  /** 참가 종별('mars' | 'fmu' | …) — 어느 패널·스트림 스펙을 쓸지의 근거 */
  kind?: string;
}

/** 실행 모드 어휘 — SM 의 sim_type 과 이력의 runMode 가 같은 값을 쓴다. 화면 라벨은 여기 한 곳(단독·커플링). */
export type RunMode = 'single' | 'cosim';
/** 실행 모드의 화면 라벨 정본 — 새 모드는 RunMode 와 여기에 한 줄씩 */
export const RUN_MODE_LABEL: Record<RunMode, string> = { single: '단독', cosim: '커플링' };
/** 화면 라벨 — 아는 값은 선언된 라벨, 모르는 값은 서버가 준 원문 그대로(감추지도 날조하지도 않는다), 빈 값은 '' */
export function runModeLabel(mode: string | null | undefined): string {
  if (!mode) return '';
  return Object.prototype.hasOwnProperty.call(RUN_MODE_LABEL, mode) ? RUN_MODE_LABEL[mode as RunMode] : mode;
}

/**
 * 이력 원문 상태 → 기록 상태. 기록 상태는 세션 어휘(`SimStatus`) 그대로다 — 어휘 밖의 원문은 «상태 모름»(unknown)이고 원문은
 * `rawStatus` 에 그대로 남는다(지어내지 않는다). 낱말·톤·아이콘은 상태 표현 정본(shared/ui/runStatusView) 하나다.
 * 예전에는 표시용 대문자 어휘(Building·Running·Completed …)를 따로 두고 표로 세션 어휘와 오갔으며, 표 밖의 원문은 «완료» 로 뭉갰다(D-115).
 */
export function entryStatusFromServer(raw: string): SimStatus {
  const status = String(raw || '').trim().toLowerCase();
  return isSimStatus(status) ? status : 'unknown';
}

/** 아직 끝나지 않은 실행의 기록(짓는 중·진행 중) — 이 기록의 카드·이어보기는 세션(실행 화면)이 맡는다. 끝난 기록 목록은 이것을 거른다 */
export const entryIsLive = (entry: Pick<SimulationEntry, 'status'>): boolean => isInProgressSimStatus(entry.status);

/**
 * 이력이 마감됐는가 — 기록 상태가 종결 어휘(types/simulation)일 때. 짓는 중·일시정지·«상태 모름»(어휘 밖 원문, URL 만으로 합성한
 * 항목)은 마감이 아니다 — 계속 받아 본다(다시 받을 때는 진짜 이력을 읽어 원문이 생긴다).
 */
export function entryIsFinal(entry: Pick<SimulationEntry, 'status'> | null | undefined): boolean {
  return !!entry && TERMINAL_SIM_STATUSES.includes(entry.status);
}

/** 참가자(task_id)의 판정 — 저널 v3 라벨이 세션 어휘면 그것, 모르는 라벨은 null(모름). 참가자 판정이 없는 구 이력은
 *  실행 판정을 참가자 판정으로 */
export function entryParticipantVerdict(
  entry: Pick<SimulationEntry, 'status' | 'rawStatus' | 'participants'>,
  taskId: string,
): SimStatus | null {
  const participants = entry.participants ?? [];
  if (participants.length === 0) return entry.status;
  const label = participants.find((p) => p.taskId === taskId)?.finalState;
  return isSimStatus(label) ? label : null;
}

export interface SimulationEntry {
  id: string;                // UUID
  name: string;              // "LOFW Simulation"
  timestamp: string;         // ISO timestamp
  duration: string;          // "3 sec"
  timeRange: string;         // "13:00:00 ~ 14:00:00"
  /** 기록 상태 — 세션 어휘. 서버 원문이 어휘 밖이면 «상태 모름»(entryStatusFromServer) */
  status: SimStatus;
  modelId: string;           // 연관된 모델 ID
  // Co-Sim 확장 필드
  simId?: string;            // Co-Sim 시뮬레이션 ID
  taskIds?: string[];        // Co-Sim 내 각 모델의 task ID 목록
  /** 참가 모델 ID 목록 (task_ids 동순) — 구 이력(귀속 미기록)은 빈 목록 */
  modelIds?: string[];
  /** 실행 모드 — RunMode | ''(구 이력 미기록). 화면 라벨은 runModeLabel 한 곳 */
  runMode?: RunMode | '';
  /** 서버 원문 상태(소문자) — 서버가 상태를 실었다는 사실과 어휘 밖의 원문을 든다(그때 status 는 «상태 모름»). URL 만으로
   *  합성한 항목에는 없다. 파괴적 판단(자동 정리 등)은 이 원문의 명시 허용목록으로 한다 */
  rawStatus?: string;
  /** 소속 프로젝트 — 세션 [이어보기]의 역방향 탐색(sim→project)에 필요 (#288) */
  projectId?: string;
  /** 참가자(task_id)별 종결 판정 — BFF simulation_history.participant_verdicts(SM 종결 저널 v3). 결과 파일 유무는
   *  실행 상태가 아니라 이것으로 판단한다(정지 핸드셰이크 결정 2). 구 이력·미기록은 빈 목록 */
  participants?: ReadonlyArray<ParticipantVerdict>;
  /** 참가자 신원(태스크별 모델 id·이름·종별)의 시작 시점 스냅샷 — 이어보기·결과 보기는 프로젝트가 아니라 이것으로
   *  참가자를 부른다. 구 이력·미기록은 빈 목록 */
  participantSpecs?: ReadonlyArray<ParticipantSpec>;
  /** 종결 시각(ISO, BFF simulation_history.end_time) — 기록으로 실행을 닫을 때 종료 시각의 정본. 마감 전이면 없다 */
  endedAt?: string;
  /** 이력 생성 시점의 프로젝트 이름 스냅샷(BFF simulation_history.project_name) — 프로젝트가 지워진 뒤에도
   *  이름을 보이기 위한 것. 살아 있는 프로젝트는 적재된 현재 이름이 우선(개명 반영). 구 이력은 '' */
  projectName?: string;
  /** 이 실행이 고른 시나리오의 id·이름 — 시작 시점 스냅샷(BFF simulation_history.scenario_id·scenario_name). 시나리오 없는
   *  실행·구 이력은 없다 */
  scenarioId?: string;
  scenarioName?: string;
}

// 모델 인터페이스 (프로젝트 내 개별 모델)


/**
 * 직렬화 경계의 노드·엣지·설정 — 저장소(JSON)와 오가는 형태라 구조를 여기서
 * 고정하지 않는다(정본은 ReactFlow Node/GlobalSettings). `any` 는 소비처의
 * 오타·오접근까지 통과시키므로 금지 — 필요한 쪽이 자기 계약 타입으로 좁힌다.
 */
export type SerializedNode = Record<string, unknown>;
export type SerializedEdge = Record<string, unknown>;
export type SerializedSettings = Record<string, unknown>;
export type SerializedSvgItem = Record<string, unknown>;

/**
 * RESTART 기준선 — 원본 실행이 남긴 짝(`baseline.i`)에서 **한 번** 판 그래프.
 *
 * 모델의 nodes/edges 와 같은 파생본이라 신원이 맞는다. 다시 파생하는 지점은
 * 없다. 덱 원문은 들지 않는다 — 파서가 분류하지 못하는 카드가 있는 덱은
 * 애초에 재시작 원본으로 받지 않기 때문이다(설계 §5).
 */
export interface RestartBaseline {
  nodes: SerializedNode[];
  edges: SerializedEdge[];
  capturedAt: number;
  /** 재시작이 이어 도는 문제 시각(초) = 원본 실행이 rstplt 에 남긴 그 블록의 시각. 기준선을 뜰 때 원본 outdta 의
   *  블록 기록 줄에서 읽는다(카드 103 W1 규칙). 읽지 못했으면 없음 — 진행률의 시작을 모른다(날조하지 않는다). */
  restartTime?: number;
}

export interface Model {
  id: string;                       // UUID
  name: string;                     // "Reactor Coolant System"
  analysisCodes: AnalysisCode[];    // 복수 선택: ['MARS', 'GAMMA+']
  description: string | null;
  scope: ModelScope;                // 담당(cover) 스코프 — 모델 간 겹침 허용
  role?: ModelRole;                 // 참가자 역할 — 없으면 system(계통 모델). control/scenario 는 scope=작용 계통(선택), 커버리지 제외
  nodes: SerializedNode[];          // ReactFlow 노드
  edges: SerializedEdge[];          // ReactFlow 엣지
  settings: SerializedSettings;     // 모델별 설정 (NEW 해석용 전체)
  restartSettings?: SerializedSettings; // RESTART 오버라이드 설정 (오버라이드 카드만)
  /** RESTART 기준선 — 원본 **실행의 짝**. 생성 시 1회 기록하고 이후 바뀌지 않는다.
   *  설계: `docs/DESIGN-restart-baseline-pairing.md` §4.1 */
  restartBaseline?: RestartBaseline;
  svgLibrary?: SerializedSvgItem[]; // SVG 라이브러리 아이템 목록
  defaultSvgByType?: Record<string, string>;  // 컴포넌트 타입별 기본 SVG ID
  nmlCouplingConfig?: NmlCouplingConfig;  // Co-Sim NML 커플링 설정
  textContent?: string;             // 원문(.i 덱 또는 Modelica .mo) — 통합 모드의 SSOT (GUI 저장 시 머지 유지)
  /** 계통 구조 선언(§11.7) — 초기 배치 X축 품질용 GUI 메타데이터 {datum, separator, risingLeg} */
  systemDeclaration?: { datum?: string; separator?: string; risingLeg?: string };
  updateHistory: VersionEntry[];    // 모델 버전 히스토리
  created_at: string;               // ISO timestamp
  updated_at: string;               // ISO timestamp
}

// 새 모델 생성 시 필요한 필드
export interface ModelInsert {
  name: string;
  analysisCodes: AnalysisCode[];
  description?: string | null;
  scope: ModelScope;
  role?: ModelRole;
  nodes?: SerializedNode[];
  edges?: SerializedEdge[];
  settings?: SerializedSettings;
  restartSettings?: SerializedSettings;
  nmlCouplingConfig?: NmlCouplingConfig;
  textContent?: string;             // 신규
}

// 모델 업데이트 시 사용할 필드
export interface ModelUpdate {
  name?: string;
  analysisCodes?: AnalysisCode[];
  description?: string | null;
  scope?: ModelScope;
  role?: ModelRole;
  nodes?: SerializedNode[];
  edges?: SerializedEdge[];
  settings?: SerializedSettings;
  restartSettings?: SerializedSettings;
  /** RESTART 기준선 — 그래프와 한 쌍이다. 그래프만 갈아끼우고 이 값을 두면
   *  신원이 갈려 편집 없이 전 컴포넌트가 삭제로 잡힌다(#460). */
  restartBaseline?: RestartBaseline;
  svgLibrary?: SerializedSvgItem[];
  defaultSvgByType?: Record<string, string>;
  nmlCouplingConfig?: NmlCouplingConfig;
  textContent?: string;             // 원문(.i 덱 또는 Modelica .mo) — GUI 저장 시 머지 결과로 갱신
  systemDeclaration?: { datum?: string; separator?: string; risingLeg?: string };
}

/** 모델 업데이트 입력 — 값, 또는 발신 시점의 모델에서 갱신 필드를 만드는 함수(projectStore.updateModel) */
export type ModelUpdateInput = ModelUpdate | ((model: Model) => ModelUpdate);

// ============================================
// Workspace 타입 정의 — 프로젝트 상위 그룹
// ============================================

/**
 * 워크스페이스 — 프로젝트 목록의 상위 그룹 (사용자별).
 * 기본 워크스페이스는 사용자마다 하나 있는 **행**(is_default, bff 0017) — 워크스페이스를 고르지 않은 프로젝트가 속하고,
 * 워크스페이스 삭제 시 소속 프로젝트는 그 행으로 옮겨진다(비파괴). 지울 수 없고 이름은 바꿀 수 있다.
 * «무소속(workspace_id 빈 값)» 은 없다.
 */
export interface Workspace {
  id: string; // UUID
  user_id: string;
  name: string; // 사용자 내 유일
  description: string;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  /** 사용자의 기본 워크스페이스 행인가 — 판별·순서는 app/workspaceFilter 하나 */
  is_default: boolean;
}

// ============================================
// Project 타입 정의
// ============================================

// Projects 테이블 Row 타입 (DB에서 조회 시)
export interface Project {
  id: string; // UUID
  user_id: string; // auth.uid()
  name: string;
  description: string | null;
  /** @deprecated Category는 더 이상 사용하지 않음 */
  category?: ProjectCategory;
  data: ProjectData | null; // JSONB
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  /** 소속 워크스페이스 id — 항상 있다(소속 없는 프로젝트는 없다, bff 0017) */
  workspace_id: string;
}

// 프로젝트 생성 시 필요한 필드
export interface ProjectInsert {
  name: string;
  description?: string | null;
  /** @deprecated Category는 더 이상 사용하지 않음 */
  category?: ProjectCategory;
  data?: ProjectData | null;
  /** 생성 시 소속 워크스페이스 — 부재/빈 값이면 서버가 호출자의 기본 워크스페이스에 만든다 */
  workspace_id?: string;
  // user_id는 RLS에서 자동으로 auth.uid() 사용
}

// 프로젝트 업데이트 시 사용할 필드
export interface ProjectUpdate {
  name?: string;
  description?: string | null;
  /** @deprecated Category는 더 이상 사용하지 않음 */
  category?: ProjectCategory;
  data?: ProjectData | null;
}

// Project Scope 타입 (PRJ-001-NP) - SystemScope의 별칭으로 유지 (하위 호환)
export type ProjectScope = SystemScope;

// Project Partition 설정 (PRJ-001-NP)
export interface ProjectPartition {
  nsssModel?: boolean;
  bopModel?: boolean;
  analysisCode?: AnalysisCode | string;
}

// ============================================
// ProjectData 타입 (JSONB 컬럼)
// ============================================

/**
 * 프로젝트 데이터 (JSONB) — 신구조만.
 *
 * NOTE (2026-04-28): legacy 필드 (`nodes`, `edges`, `globalSettings`,
 * `metadata`) 제거됨. silent 마이그레이션 사고 후 backwards-compat 분기는
 * 데이터 손실 위험만 만들어 모두 제거.
 *
 * `metadata` 안에 있던 `taskMode`, `restartProjectId/SourceTaskId/SimulationId`,
 * `tags` 등 살아남은 필드는 모델 또는 프로젝트 레벨로 이전 필요. 현재
 * 코드에서 참조 시 컴파일 에러로 surface 됨.
 */
export interface ProjectData {
  totalScope?: ModelScope;                    // 프로젝트 전체 스코프
  models?: Model[];                           // 모델 목록
  updateHistory?: VersionEntry[];             // 프로젝트 버전 히스토리
  // 실행 기록은 본문에 두지 않는다 — 정본은 BFF 의 실행 기록(simulation_history). 본문의 옛 배열(simulationHistory)은
  // 아무도 쓰지 않는데 카드 수·조회 실패 대체가 그것을 읽어 «0» 을 지어냈다(6차 베타 2026-10-15). 옛 저장본에 남은 키는 읽지 않는다
  coSimConfig?: CoSimConfig;                  // Co-Sim 프로젝트 레벨 설정
  nodeSizingRules?: NodeSizingRules;          // MARS .i 임포트 노드 크기 파생 규칙
  /** 시나리오 — 모델·커플링 설정과 분리된 실행 입력(어느 입력을 언제 어떻게 바꾼다). 프로젝트 소유, 실행 때 하나를 고른다. `domain/scenario` */
  scenarios?: Scenario[];
}

/**
 * 프로젝트의 **저작 내용** — 사용자가 만든 것. 이력(버전·실행)은 그 프로젝트에서 일어난 일이라 저작이 아니다.
 * 예제 설치기의 «수정했는가» 대조와 «JSON 파일에서 새 프로젝트 만들기» 가 같은 선언을 본다.
 */
export const AUTHORED_PROJECT_KEYS = ['totalScope', 'models', 'coSimConfig', 'nodeSizingRules', 'scenarios'] as const satisfies readonly (keyof ProjectData)[];

/** 저장본에 남았을 수 있지만 이제 읽지도 쓰지도 않는 모델 키 — 옛 실행이 적던 FMU 기록(`fmuArtifact`). FMU 는 모델 폴더의
 *  빌드 캐시가 저장소에서 정본이다(`app/authoring/fmuBuildCache`, docs/DESIGN-run-input-lifecycle.md §6.4) */
type RetiredModelKey = 'fmuArtifact';

/**
 * 모델에서 **매번 새로 발급되거나 실행이 적던 파생값** — 저작 내용이 아니다.
 * `id`·시각은 만들 때 새로 찍는다. `fmuArtifact` 는 옛 저장본에 남은 기록이라(다른 프로젝트의 산출물을 가리킨다) 새로
 * 만들거나 대조할 때 버린다.
 */
export const VOLATILE_MODEL_KEYS = ['id', 'created_at', 'updated_at', 'fmuArtifact'] as const satisfies readonly (keyof Model | RetiredModelKey)[];

/**
 * 프로젝트 목록 항목 — 서버(BFF ListProjects)가 본문(data) 대신 싣는 요약. 카드가 보여 줄 개수는 서버가 jsonb 에서
 * 센다(본문 전량을 싣던 목록이 워크스페이스 전환마다 2.5MB·4.5초였다, 게시판 #34). 본문이 필요한 곳(열기·다른
 * 프로젝트의 모델 복제)은 그때 GetProject 로 받는다. 클라이언트가 목록 항목을 갱신할 때는 summarizeProject 로 같은
 * 규칙을 적용한다.
 */
export interface ProjectSummary {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  /** 소속 워크스페이스 id — 항상 있다(소속 없는 프로젝트는 없다, bff 0017) */
  workspace_id: string;
  /** data.models 의 수 */
  model_count: number;
  /** 모든 모델의 nodes 합 */
  node_count: number;
  /** 실행 기록 수 — 서버가 실행 기록(simulation_history)에서 센다. 본문에서 파생하지 않는다 */
  simulation_count: number;
  /** 모델당 1항목(모델 순서 유지): 그 모델의 analysisCodes 를 '/' 로 이은 것, 빈 모델 제외 */
  analysis_codes: string[];
}

// ============================================
// 프로젝트 생성 폼 타입 (NewProjectWizard → ProjectPickerPage)
// ============================================

/**
 * 프로젝트 생성 시 모델(파티션) 정의
 * - 각 모델은 프로젝트 스코프의 일부를 담당(cover)
 * - 모델 간 스코프 겹침 허용(배타 아님), 단 union 이 전체 스코프를
 *   빠짐없이 cover 해야 함 (커버리지 보장)
 */
export interface PartitionFormData {
  id: string;                    // 임시 ID (폼 내 식별용)
  name: string;                  // 모델 이름: "NSSS Model"
  analysisCode: AnalysisCode;    // 분석 코드: MARS | GAMMA+ | Modelica
  description?: string;          // 모델 설명 (선택)
  scope: SystemScope[];          // 모델이 cover 하는 스코프 (빈 배열 허용)
  role?: ModelRole;              // 참가자 역할 (없으면 system)
  marsConfig?: MARSConfig;       // MARS 설정 (MARS 선택 시만 사용)
}

/**
 * 새 프로젝트 생성 폼 데이터
 */
export interface NewProjectFormData {
  title: string;                       // 프로젝트 이름
  description: string;                 // 프로젝트 설명
  scope: SystemScope[];                // 전체 프로젝트 스코프
  partitions: PartitionFormData[];     // 모델(파티션) 목록
}
